import { useRef, useState } from "react";
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js/max";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, HeartHandshake, Sparkles, Lock, Mail } from "lucide-react";

const TABS = [
  {
    key: "prayer_request",
    label: "Prayer Request",
    icon: HeartHandshake,
    tagline: "Private \u2014 shared only with our pastor and prayer team",
    verse: "\u201CCast all your anxiety on him because he cares for you.\u201D \u2014 1 Peter 5:7",
    placeholder: "How can we pray for you? Your request stays private.",
    submitLabel: "Send Request",
    successToast: "Thank you \u2014 we will pray with you.",
    fieldLabel: "Your Request",
  },
  {
    key: "praise",
    label: "Item of Praise",
    icon: Sparkles,
    tagline: "Testify to what the Lord has done \u2014 optionally share publicly (admin-reviewed)",
    verse: "\u201COh give thanks unto the Lord; for he is good: for his mercy endureth for ever.\u201D \u2014 Psalm 107:1",
    placeholder: "Tell us how the Lord has answered prayer or blessed you...",
    submitLabel: "Share Praise",
    successToast: "Praise God \u2014 thank you for sharing!",
    fieldLabel: "Share What God Has Done",
  },
  {
    key: "message",
    label: "Message",
    icon: Mail,
    tagline: "General questions, feedback, or a friendly hello \u2014 our staff will reply",
    verse: "\u201CBe not forgetful to entertain strangers\u2026\u201D \u2014 Hebrews 13:2",
    placeholder: "Have a question, want to visit, or just want to say hi? Send us a message.",
    submitLabel: "Send Message",
    successToast: "Message received \u2014 we\u2019ll be in touch soon.",
    fieldLabel: "Your Message",
  },
];

const countryNames = new Intl.DisplayNames(["en"], { type: "region" });
const countries = getCountries().map((code) => ({
  code, callingCode: `+${getCountryCallingCode(code)}`,
  label: `${countryNames.of(code)} (+${getCountryCallingCode(code)})`,
})).sort((a, b) => a.label.localeCompare(b.label));
const validEmail = (value) => /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]{2,}$/u.test(value);
const contactMessage = "Please provide a valid email address or phone number so we can contact you.";

function validateContact(form) {
  const errors = {};
  const email = form.email.trim();
  const phone = form.phone_number.trim();
  if (email && !validEmail(email)) errors.email = "Please enter a valid email address.";
  if (phone) {
    const country = countries.find((item) => item.code === form.country);
    const parsed = parsePhoneNumberFromString(phone, phone.startsWith("+") ? undefined : country?.code);
    if (!parsed?.isValid()) errors.phone = "Please enter a valid phone number for the selected country.";
    if (!phone.startsWith("+") && !country) errors.phone = "Select a country for your phone number.";
  }
  if (!email && !phone) errors.contact = contactMessage;
  return errors;
}

export default function PrayerForm() {
  const [kind, setKind] = useState("prayer_request");
  const [form, setForm] = useState({ name: "", email: "", country: "PH", phone_number: "", subject: "", request: "", is_public: false });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const contactRef = useRef(null);

  const active = TABS.find((t) => t.key === kind);
  const Icon = active.icon;

  const switchKind = (k) => {
    setKind(k);
    setErrors({});
    setForm((f) => ({ ...f, is_public: false }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.request.trim()) {
      toast.error("Please share your name and message.");
      return;
    }
    if (kind === "message") {
      const nextErrors = validateContact(form);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length) {
        contactRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        contactRef.current?.querySelector("input")?.focus();
        return;
      }
    }
    setSubmitting(true);
    try {
      const country = countries.find((item) => item.code === form.country);
      await api.post("/prayers", {
        name: form.name.trim(), email: form.email.trim(), request: form.request.trim(), is_public: form.is_public,
        kind, source: "website", ...(kind === "message" ? {
          subject: form.subject.trim(), country_code: country?.callingCode, phone_number: form.phone_number.trim(),
        } : {}),
      });
      toast.success(active.successToast);
      setForm({ name: "", email: "", country: "PH", phone_number: "", subject: "", request: "", is_public: false });
      setErrors({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="prayer" data-testid="prayer-section" className="py-24 lg:py-32 bg-[#F3EFEA]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-8">
          <Icon className="w-8 h-8 mx-auto mb-4 text-[#2C4C3B]" />
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C201E]">Prayer, Praise &amp; Messages</h2>
          <p className="mt-3 text-[#5C6661] italic">{active.verse}</p>
        </div>

        <div className="flex justify-center mb-6" role="tablist">
          <div className="inline-flex bg-white border border-[#E5E0D8] rounded-full p-1 flex-wrap justify-center">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={kind === t.key}
                data-testid={`prayer-tab-${t.key}`}
                onClick={() => switchKind(t.key)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${kind === t.key ? "bg-[#2C4C3B] text-white" : "text-[#1C201E] hover:bg-[#F3EFEA]"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} noValidate data-testid={`prayer-form-${kind}`} className="p-8 md:p-12 bg-[#FAF9F6] border border-[#E5E0D8] rounded-3xl space-y-5">
          <div data-testid={`prayer-tab-tagline-${kind}`} className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] ${kind === "prayer_request" ? "text-[#2C4C3B]" : kind === "praise" ? "text-[#8a5a2a]" : "text-[#2C4C3B]"}`}>
            {kind === "prayer_request" && <Lock className="w-3 h-3" />}
            {kind === "message" && <Mail className="w-3 h-3" />}
            {active.tagline}
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#5C6661]">Your Name</label>
              <input
                data-testid="prayer-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-white border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373] focus:border-transparent"
                placeholder="Jane Doe"
              />
            </div>
            <div ref={contactRef}>
              <label htmlFor="visitor-email" className="text-xs uppercase tracking-[0.2em] text-[#5C6661]">Email Address {kind !== "message" && "(optional)"}</label>
              <input
                id="visitor-email"
                data-testid="prayer-email"
                type="email"
                value={form.email}
                onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrors((old) => ({ ...old, email: "", contact: "" })); }}
                onBlur={() => kind === "message" && setErrors((old) => ({ ...old, email: form.email.trim() && !validEmail(form.email.trim()) ? "Please enter a valid email address." : "" }))}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "visitor-email-error" : undefined}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-white border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373] focus:border-transparent"
                placeholder="you@example.com"
              />
              {errors.email && <p id="visitor-email-error" role="alert" className="mt-1 text-sm text-red-700">{errors.email}</p>}
            </div>
          </div>
          {kind === "message" && <div className="space-y-4 rounded-xl border border-[#E5E0D8] bg-white/70 p-4">
            <div><h3 className="font-semibold text-[#1C201E]">Contact Information</h3><p className="text-sm text-[#5C6661]">Provide at least one: Email or Phone Number.</p></div>
            {errors.contact && <p role="alert" className="text-sm text-red-700">{errors.contact}</p>}
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
              <label className="text-sm text-[#5C6661]">Country Code
                <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E5E0D8] bg-white px-3 py-3 text-[#1C201E]">
                  {countries.map((country) => <option key={country.code} value={country.code}>{country.label}</option>)}
                </select>
              </label>
              <label className="text-sm text-[#5C6661]">Phone Number
                <input type="tel" autoComplete="tel-national" inputMode="tel" value={form.phone_number} onChange={(e) => { setForm({ ...form, phone_number: e.target.value }); setErrors((old) => ({ ...old, phone: "", contact: "" })); }} onBlur={() => setErrors((old) => ({ ...old, phone: validateContact(form).phone || "" }))} aria-invalid={!!errors.phone} placeholder="917 123 4567" className="mt-1 w-full rounded-xl border border-[#E5E0D8] bg-white px-3 py-3 text-[#1C201E]" />
              </label>
            </div>
            {errors.phone && <p role="alert" className="text-sm text-red-700">{errors.phone}</p>}
            <label className="block text-sm text-[#5C6661]">Subject (optional)
              <input value={form.subject} maxLength={256} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 w-full rounded-xl border border-[#E5E0D8] bg-white px-3 py-3 text-[#1C201E]" placeholder="How can we help?" />
            </label>
          </div>}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#5C6661]">{active.fieldLabel}</label>
            <textarea
              data-testid="prayer-request"
              rows={5}
              value={form.request}
              onChange={(e) => setForm({ ...form, request: e.target.value })}
              className="mt-2 w-full px-4 py-3 rounded-xl bg-white border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373] focus:border-transparent resize-none"
              placeholder={active.placeholder}
            />
          </div>

          {kind === "praise" && (
            <label className="flex items-start gap-3 text-sm text-[#5C6661] bg-[#D4A373]/10 border border-[#D4A373]/30 rounded-xl p-4">
              <input
                data-testid="prayer-public"
                type="checkbox"
                checked={form.is_public}
                onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                className="mt-1 w-4 h-4 accent-[#2C4C3B]"
              />
              <span>
                <b className="text-[#1C201E]">I consent to have this praise shared publicly</b> on the church website&rsquo;s <em>Items of Praise</em> wall, so the congregation can rejoice with me. <b>Every submission is reviewed by our staff before it appears publicly.</b> If unchecked, only the pastor and prayer team will see it.
              </span>
            </label>
          )}
          {kind === "prayer_request" && (
            <div className="flex items-start gap-3 text-sm text-[#5C6661] bg-[#2C4C3B]/5 border border-[#2C4C3B]/20 rounded-xl p-4">
              <Lock className="w-4 h-4 mt-0.5 text-[#2C4C3B] shrink-0" />
              <span>
                Prayer requests are <b>never displayed on this website</b>. Only our pastor and prayer team receive them (and members of the upcoming <b>BBC Connect</b> app).
              </span>
            </div>
          )}
          {kind === "message" && (
            <div className="flex items-start gap-3 text-sm text-[#5C6661] bg-[#FAF9F6] border border-[#E5E0D8] rounded-xl p-4">
              <Mail className="w-4 h-4 mt-0.5 text-[#2C4C3B] shrink-0" />
              <span>Messages go to our staff inbox and are not shown on the site. Provide a valid email address or phone number so we can reply.</span>
            </div>
          )}

          <button
            data-testid="prayer-submit"
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] transition-colors duration-200 disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Sending..." : active.submitLabel}
          </button>
        </form>
      </div>
    </section>
  );
}
